import { useEffect, useState } from 'react';
import background from '../assets/images/blue.png';
import dots from '../assets/images/dots.png';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { IoEyeOutline } from "react-icons/io5";
import { PiEyeSlash } from "react-icons/pi";
import { useTranslation } from 'react-i18next';
import useTitle from '../Hooks/useTitle';
import { resetPassword } from '../Redux/Action/AuthAction';
import { useLocation, useNavigate } from 'react-router-dom';

const ResetPassword = () => {
    useTitle('Reset Password');
    const { t } = useTranslation('resetpassword');
    const { loading } = useSelector((state) => state.Auth);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [showCPassword, setShowCPassword] = useState(false);

    const [credentials, setCredentials] = useState({
        password: '',
        password_confirmation: ''
    });

    // ✅ Get query params
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const token = queryParams.get('token');
    const email = queryParams.get('email');

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleCPasswordVisibility = () => setShowCPassword(!showCPassword);

    const handleSubmit = (e) => {
        e.preventDefault();

        const { password, password_confirmation } = credentials;

        if (!password || !password_confirmation) {
            toast.error(t("error_required"));
            return;
        }
        if (password !== password_confirmation) {
            toast.error(t("error_mismatch"));
            return;
        }

        dispatch(resetPassword({
            token,
            email,
            password,
            password_confirmation
        }))
            .then(() => {
                toast.success(t("success_message"));
                navigate('/');
            })
            .catch((error) => {
                toast.error(
                    Object.values(error.response?.data?.errors || {})[0]?.[0] || t("error_generic")
                );
            });
    };

    // If token/email is missing, redirect or show error
    useEffect(() => {
        if (!token || !email) {
            toast.error("Invalid reset link.");
            navigate('/');
        }
    }, [token, email, navigate]);

    return (
        <div className="flex w-[100vw] h-[100vh]">
            <div className="hidden w-[35vw] h-[100vh] relative sm:hidden lg:block">
                <img src={background} alt="background" className='h-[100vh] w-full object-cover' />
                <div className='absolute top-10 right-[10%]'>
                    <img src={dots} alt="dots" />
                </div>

                <div className="absolute lg:top-[30%] xl:top-[35%] text-center grid gap-5 w-full xl:p-[50px] md:p-[30px]">
                    <h1 className='font-[600] text-[40px] text-white'>{t('main_title')}</h1>
                    <p className='text-white font-[300] text-[15px]'>{t("description")}</p>
                </div>

                <div className='absolute bottom-10 left-[10%]'>
                    <img src={dots} alt="dots" />
                </div>
            </div>

            <div className="w-full min-h-screen flex justify-center items-center bg-[#EDF4FF]">
                <div className="w-full mx-auto bg-[#EDF4FF] px-[30px] py-[40px] lg:w-[65vw]">
                    {
                        loading ? (
                            <div className="w-full h-[100vh] flex justify-center items-center">
                                <CircularProgress size="3rem" />
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="bg-white p-[20px] sm:p-[40px] rounded-[20px] w-full mt-[300px] sm:mt-0">
                                <div className='grid gap-[32px]'>
                                    <div className="text-center w-full">
                                        <h1 className='font-[700] text-[#131415] text-[26px]'>{t("title")}</h1>
                                        <p className='text-[#535861]'>{t("subtitle")}</p>
                                    </div>
                                    <div className="grid gap-4 w-full sm:grid-cols-1 sm:w-[80%] mx-auto">
                                        <div className="flex flex-col gap-2 relative">
                                            <span className='text-[14px] font-[500] text-[#131415]'>{t("password_label")}</span>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder={t("password_placeholder")}
                                                className='border border-[#c3cbd7] py-[10px] rounded-[10px] px-2 text-[14px] w-full'
                                                name="password"
                                                value={credentials.password}
                                                onChange={(e) => setCredentials({ ...credentials, [e.target.name]: e.target.value })}
                                            />
                                            {showPassword ? (
                                                <IoEyeOutline className='absolute top-11 right-3 text-black cursor-pointer' onClick={togglePasswordVisibility} />
                                            ) : (
                                                <PiEyeSlash className='absolute top-11 right-3 text-black cursor-pointer' onClick={togglePasswordVisibility} />
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2 relative">
                                            <span className='text-[14px] font-[500] text-[#131415]'>{t("confirm_password_label")}</span>
                                            <input
                                                type={showCPassword ? 'text' : 'password'}
                                                placeholder={t("confirm_password_placeholder")}
                                                className='border border-[#c3cbd7] py-[10px] rounded-[10px] px-2 text-[14px] w-full'
                                                name="password_confirmation"
                                                value={credentials.password_confirmation}
                                                onChange={(e) => setCredentials({ ...credentials, [e.target.name]: e.target.value })}
                                            />
                                            {showCPassword ? (
                                                <IoEyeOutline className='absolute top-11 right-3 text-black cursor-pointer' onClick={toggleCPasswordVisibility} />
                                            ) : (
                                                <PiEyeSlash className='absolute top-11 right-3 text-black cursor-pointer' onClick={toggleCPasswordVisibility} />
                                            )}
                                        </div>
                                    </div>
                                    <button type="submit" className='bg-[#3686ff] text-white max-w-40 w-full h-[52px] rounded-[15px] mx-auto text-center flex items-center justify-center'>
                                        {t("submit_button")}
                                    </button>
                                </div>
                            </form>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
